import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactQuill, { Quill } from 'react-quill';
import Delta from 'quill-delta';
// import quillEmoji from 'quill-emoji/dist/quill-emoji';
import { EmojiBlot, ToolbarEmoji } from './quill-emoji/src/quill-emoji.js';
import './emoji.css';
import './quill.snow-custom.css';
import './TextEditor.css';
import FileUploader from './FileUploader';

/*
 * пример взял на
 * https://codepen.io/alexkrolick/pen/gmroPj?editors=0010
 */
const CustomToolbar = () => (
  <div id="ql-custom-toolbar" className="textarea__action-list">
    <button className="ql-bold button--circle textarea__action" tabIndex="1">
      <img src="/Content/images/btns/edtor1.svg" alt="Жирный текст" />
      <div className="button__hint">Жирный текст</div>
    </button>
    <button className="ql-italic button--circle textarea__action" tabIndex="1">
      <img src="/Content/images/btns/edtor2.svg" alt="Курсив" />
      <div className="button__hint">Курсив</div>
    </button>
    <button className="ql-underline button--circle textarea__action" tabIndex="1">
      <img src="/Content/images/btns/edtor3.svg" alt="Подчеркивание" />
      <div className="button__hint">Подчеркивание</div>
    </button>
    <button className="ql-attachFile button--circle textarea__action" tabIndex="1">
      <img src="/Content/images/btns/edtor4.svg" alt="Прикрепить файл" />
      <div className="button__hint">Прикрепить файл</div>
    </button>
    <button className="ql-link button--circle textarea__action" tabIndex="1">
      <img src="/Content/images/btns/edtor5.svg" alt="Добавить ссылку" />
      <div className="button__hint">Добавить ссылку</div>
    </button>
    <button className="ql-addPicture button--circle textarea__action" tabIndex="1">
      <img src="/Content/images/btns/edtor6.svg" alt="Добавить картинку" />
      <div className="button__hint">Добавить картинку</div>
    </button>
    <button className="ql-addVideo button--circle textarea__action" tabIndex="1">
      <img src="/Content/images/btns/edtor7.svg" alt="Добавить видео" />
      <div className="button__hint">Добавить видео</div>
    </button>
    <button className="ql-emoji button--circle textarea__action" tabIndex="1">
      <img src="/Content/images/btns/edtor8.svg" alt="Добавить emoji" />
      <div className="button__hint">Добавить Emoji</div>
    </button>
    <button className="ql-clean button--circle textarea__action" tabIndex="1">
      <img src="/Content/images/btns/edtor9.svg" alt="Удалить форматирование текста" />
      <div className="button__hint">Удалить форматирование текста</div>
    </button>
  </div>
);

const icons = Quill.import('ui/icons');
icons.bold = `<img src="/Content/images/btns/edtor1.svg" alt="Жирный текст" />
<div class="button__hint">Жирный текст</div>`;
icons.italic = `<img src="/Content/images/btns/edtor2.svg" alt="Курсив" />
<div class="button__hint">Курсив</div>`;
icons.underline = `<img src="/Content/images/btns/edtor3.svg" alt="Подчеркивание" />
<div class="button__hint">Подчеркивание</div>`;
icons.link = `<img src="/Content/images/btns/edtor5.svg" alt="Добавить ссылку" />
<div class="button__hint">Добавить ссылку</div>`;
icons.clean = `<img src="/Content/images/btns/edtor9.svg" alt="Удалить форматирование текста" />
<div class="button__hint">Удалить форматирование текста</div>`;

const Clipboard = Quill.import('modules/clipboard');

class PlainTextClipboard extends Clipboard {

  convert(html = null) {
    if (typeof html === 'string') {
      this.container.innerHTML = html;
    }
    const text = this.container.innerText;
    this.container.innerHTML = '';
    return new Delta().insert(text);
  }

  onPaste(e) {
    if (e.defaultPrevented || !this.quill.isEnabled()) return;
    const range = this.quill.getSelection();
    let delta = new Delta().retain(range.index);

    if (e && e.clipboardData && e.clipboardData.types && e.clipboardData.getData) {
      const text = (e.originalEvent || e).clipboardData.getData('text');

      const cleanedText = this.convert(text);

      // Stop the data from actually being pasted
      e.stopPropagation();
      e.preventDefault();

      // Process cleaned text
      delta = delta.concat(cleanedText).delete(range.length);
      this.quill.updateContents(delta, Quill.sources.USER);
      // range.length contributes to delta.length()
      this.quill.setSelection(delta.length() - range.length, Quill.sources.SILENT);

      return false;
    }
  }
}

Quill.register('modules/clipboard', PlainTextClipboard);

/*
 * Editor component with custom toolbar and content containers
 */
export default class TextEditor extends React.Component {
  static defaultProps = {
    allowAttachments: true,
    placeholder: '',
    editorHtml: ''
  };

  static propTypes = {
    editorHtml: PropTypes.string,
    placeholder: PropTypes.string,
    allowAttachments: PropTypes.bool,
    setAttachments: PropTypes.func.isRequired
  };

  formats = ['bold', 'italic', 'underline', 'link', 'emoji', 'clean'];

  modules = {
    toolbar: {
      container: '#ql-custom-toolbar',
      handlers: {
        attachFile: () => this.fileUploaderRef.current.uploadFiles(),
        addPicture: () => this.fileUploaderRef.current.uploadImages(),
        addVideo: () => this.fileUploaderRef.current.uploadVideos(),
        emoji: () => {}
      }
    },
    clipboard: {
      matchVisual: true
    },
    'emoji-toolbar': {
      buttonIcon:
        '<img src="/Content/images/btns/edtor8.svg" alt="Добавить emoji"/><div class="button__hint">Добавить Emoji</div>'
    }
    // 'emoji-textarea': false,
    // 'emoji-shortname': false,
  };

  constructor(props) {
    super(props);
    this.handleChange = this.handleChange.bind(this);
    this.fileUploaderRef = React.createRef();
  }

  handleChange(html) {
    this.props.onChange(html);
  }

  render() {
    const {
      editorHtml, placeholder, allowAttachments, setAttachments
    } = this.props;
    return (
      <React.Fragment>
        <div className="input-group new-theme__textarea">
          <CustomToolbar />
          <ReactQuill
            ref={(el) => {
              this.reactQuillRef = el;
            }}
            className="input-group__input input-group__input--textarea wysiwyg"
            value={editorHtml}
            onChange={this.handleChange}
            placeholder={placeholder}
            modules={this.modules}
            formats={this.formats}
            theme="snow" // pass false to use minimal theme
          />
        </div>
        {allowAttachments ? (
          <div className="input-group__attachments">
            <FileUploader ref={this.fileUploaderRef} setAttachments={setAttachments} />
          </div>
        ) : (
          ''
        )}
      </React.Fragment>
    );
  }
}
